function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
// Lib Imports
import React, { memo, useState, useMemo, useEffect, useCallback, useContext } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { Box, ThemeContext } from 'grommet';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import _ from 'lodash';
import { deepMerge } from 'grommet/utils';

// Core Imports
import useQuery from 'granite-admin/utils/useQuery';
import Loader from 'granite-admin/core/components/Loader';
import Filters from 'granite-admin/core/components/Filters';
import SideLayer from '../../components/SideLayer';
import { getGrandEmitter } from 'granite-admin/utils/grandEmitter';
import usePreferences from 'granite-admin/utils/usePreferences';
import useUtility from 'granite-admin/utils/useUtility';
import { useToast } from 'granite-admin/core/components/Toast';
import { useDeepCompareMemo, useDeepCompareEffect, useDeepCompareCallback } from '../../utils';
const emptyArray = [];
const emptyObj = {};
const dataTableDefaultProps = {
  columns: [],
  // fetch: () => {},
  filters: [],
  showFilters: false,
  queryKey: 'q',
  actionIconColor: 'brand',
  moreActionItems: [],
  exportable: [],
  importable: false,
  deleteBulk: false,
  withoutQuery: true,
  editInNewTab: false,
  onChange: () => {},
  canColumnsBeHidden: false,
  showHideColsHandler: () => {},
  addHandler: false,
  hiddenColumnArrayList: [],
  noRecordMsg: 'No Records Found',
  disableRowClicked: false,
  isLoading: false,
  disableHideCells: [],
  autoSavePreference: false,
  excludeFromPreference: [],
  searchDisabled: false,
  showPreferenceToast: false
};
const withMultiDnd = Table => {
  const Component = dataTableProps => {
    const {
      graniteTable: {
        withDataProps
      } = {}
    } = useContext(ThemeContext) ?? {};
    const mergedDefaultProps = useMemo(() => deepMerge(dataTableDefaultProps, withDataProps), [withDataProps]);
    const props = useDeepCompareMemo(() => ({
      ...mergedDefaultProps,
      ...dataTableProps
    }), [mergedDefaultProps, dataTableProps]);
    const {
      eventEmitter,
      columns,
      fetch,
      update,
      filters,
      showFilters,
      searchPlaceholder,
      queryKey,
      editHandler,
      deleteHandler,
      exportable,
      handleImport,
      actionIconColor,
      moreActionItems = emptyArray,
      sideContent,
      columnWidth,
      pageSize,
      bulkDeleteHandler,
      deleteBulk,
      sideFunctions,
      label,
      bulkActions,
      withoutQuery,
      rowClickHandler,
      newTabURL,
      editInNewTab,
      onChange: handleOnChange,
      initialState,
      canColumnsBeHidden,
      showHideColsHandler,
      hiddenColumnArrayList,
      addHandler,
      searchDisabled,
      searchWidth,
      addRowOptions,
      // panelPermissions,
      overridePermission,
      sortKeys,
      noRecordMsg,
      disableRowClicked,
      maxHeight,
      isLoading,
      disableHideCells,
      autoSavePreference,
      showPreferenceToast,
      excludeFromPreference,
      validations,
      replaceSearch,
      replaceSearchWith,
      ...rest
    } = props;
    const panelPermissions = useSelector(({
      common
    }) => common.panelPermissions.panelPermissions);
    const {
      downloadFile
    } = useUtility();
    const {
      path: url,
      params
    } = useRouteMatch();
    const {
      savePreference,
      preferences,
      fetchLoading
    } = usePreferences({
      component: queryKey,
      showPreferenceToast,
      excludeFromPreference,
      autoSavePreference
    });
    const path = useMemo(() => {
      let theUrl = url;
      Object.keys(params).forEach(key => {
        if (key.includes('sr_')) theUrl = theUrl?.replace(`:${key}`, params[key]);
      });
      return theUrl;
    }, [url, params]);
    const {
      query,
      pathname,
      createQueryString
    } = useQuery();
    const history = useHistory();
    const {
      addToast,
      successToast,
      errorToast
    } = useToast();
    const grandEmitter = useMemo(() => getGrandEmitter(), []);

    // eslint-disable-next-line no-unused-vars
    const [selected, setSelected] = useState([]);
    // eslint-disable-next-line no-unused-vars
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState({
      data: [],
      count: 0
    });
    const [newRows, setNewRows] = useState({});
    const tableCols = useMemo(() => columns, [columns]);
    const [visibleColumns, setColumns] = useState([]);
    const [sideLayer, showSideLayer] = useState(false);
    const [localFilter, setLocalFilter] = useState(initialState || {
      page: 1,
      page_size: pageSize || 10,
      is_active: true
    });
    useEffect(() => initialState && setLocalFilter(initialState), [initialState]);
    const haveEditPermission = useMemo(() => {
      const editPermission = panelPermissions[path]?.find(i => {
        if (i.type === 'table_edit' && i.extra_data?.table_name === queryKey) return i.url;
        if (!i.extra_data?.table_name && i.type === 'table_edit') return i.url;
        return false;
      });
      if (editPermission) return editPermission.url;else if (overridePermission && overridePermission['edit']) {
        return overridePermission['edit'];
      }
      // else if (overridePermission && overridePermission['delete']) {
      //   return overridePermission['delete'];
      // }
      return false;
    }, [panelPermissions, path, overridePermission, queryKey]);
    const haveDeletePermission = useMemo(() => {
      const deletePermission = panelPermissions[path]?.find(i => {
        if (i.type === 'table_delete' && i.extra_data?.table_name === queryKey) return true;
        if (!i.extra_data?.table_name && i.type === 'table_delete') return true;
        return false;
      });
      if (deletePermission) return true;else if (overridePermission && overridePermission['delete']) {
        return overridePermission['delete'];
      }
      return false;
    }, [panelPermissions, path, overridePermission, queryKey]);
    const preferencesParams = useDeepCompareMemo(() => {
      const {
        config: {
          state,
          ...otherParams
        } = emptyObj
      } = preferences ?? emptyObj;
      return otherParams;
    }, [preferences?.config ?? {}]);
    const tableUrlParams = useMemo(() => {
      if (withoutQuery) return Object.keys(preferencesParams).length > 0 ? preferencesParams : localFilter ?? {};else return query[queryKey] ? {
        page: 1,
        page_size: pageSize || 10,
        // is_active: true,
        ...JSON.parse(query[queryKey])
      } : initialState && typeof initialState === 'object' ? {
        ...initialState,
        ...preferencesParams
      } : {
        page: 1,
        page_size: pageSize || 10,
        is_active: true,
        ...preferencesParams
      };
    }, [query[queryKey], pageSize, localFilter, withoutQuery, initialState, preferencesParams]);
    const onChange = params => {
      if (autoSavePreference) {
        let pref = {
          ...tableUrlParams,
          ...params
        };
        // const filtered = Object.keys(pref)
        //   .filter(key => !excludeFromPreference.includes(key))
        //   .reduce((obj, key) => {
        //     return {
        //       ...obj,
        //       [key]: pref[key],
        //     };
        //   }, {});
        savePreference(queryKey, {
          state: preferences.config?.state,
          ...pref
        });
      }
      handleOnChange({
        ...tableUrlParams,
        ...params
      });
    };
    const actionItems = useCallback(emp => [...(haveEditPermission || editHandler ? [{
      name: 'edit',
      onClick: e => {
        e.stopPropagation();
        onEdit(emp);
      },
      color: actionIconColor
    }] : []), ...(haveDeletePermission && deleteHandler ? [{
      name: 'delete',
      onClick: () => {
        deleteHandler(emp).then(({
          showToast = true
        }) => {
          grandEmitter.emit(`${queryKey}_DELETE_ROW`, {
            pk: emp?.pk
          });
          if (showToast) {
            successToast(label && emp?.pk ? `${label} ${emp?.pk} Deleted Successfully!` : 'Deleted Successfully!');
          }
        }).catch(err => {
          if (!err.errors.hideToast) {
            errorToast(err?.errors?.title || 'Unable to Delete records !');
          }
        });
      },
      color: actionIconColor
    }] : []), ...(sideContent && emp?.pk ? [{
      name: 'view',
      onClick: e => {
        e.stopPropagation();
        showSideLayer(emp);
      },
      color: actionIconColor
    }] : []), ...(moreActionItems.length && emp?.pk ? moreActionItems : [])], [haveEditPermission, deleteHandler, actionIconColor, haveDeletePermission, moreActionItems, sideContent, grandEmitter, queryKey]);
    const navigateToParams = useCallback(params => {
      if (!tableUrlParams.ordering && !params.ordering) delete params.ordering;
      const {
        state,
        ...otherParams
      } = params;
      if (withoutQuery) {
        onChange({
          ...tableUrlParams,
          state: preferences.config?.state,
          ...params
        });
        setLocalFilter({
          ...tableUrlParams,
          ...otherParams
        });
      } else {
        let pathObj = {
          pathname,
          search: createQueryString({
            ...query,
            [queryKey]: JSON.stringify({
              ...tableUrlParams,
              ...otherParams
            })
          })
        };
        onChange({
          ...tableUrlParams,
          state: preferences.config?.state,
          ...params
        });
        history.push(pathObj);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tableUrlParams, history, query, createQueryString, queryKey, preferences.config?.state] //not including pathname as it produces bugs in tab layout
    );

    const getData = useCallback((params, hideLoader) => {
      setNewRows({});
      if (!hideLoader) setLoading(true);
      fetch(params).then(response => {
        const modifiedData = response.list.map((emp, index) => {
          // NOTE: adding employee actions for showing edit/delete buttons
          let newEmp = {
            ...emp
          };
          newEmp.uniqueId = newEmp.uniqueId ?? `${queryKey}__${emp.pk}`;
          newEmp.actions = actionItems(emp, index);
          return newEmp;
        });
        setData({
          data: modifiedData,
          count: response.count
        });
        setLoading(false);
      }).catch(err => {
        console.log(err);
        errorToast(err?.errors?.title || 'Unable to fetch records !');
        setLoading(false);
      });
    }, [fetch, actionItems, queryKey, errorToast]);
    useDeepCompareEffect(() => {
      const subscription = eventEmitter?.getObservable()?.subscribe(event => {
        switch (event.type) {
          case 'FETCH_LIST':
            {
              const newParams = event.data || {};
              getData(newParams?.mode === 'REFRESH' ? tableUrlParams : {
                ...tableUrlParams,
                page: 1
              }, newParams?.hideLoader);
              break;
            }
          case 'SET_TOAST':
            addToast(event.data.message, {
              status: event.data.color
            });
            break;
          default:
            break;
        }
      });
      if (!fetchLoading) getData(tableUrlParams);
      return () => subscription?.unsubscribe();
    }, [eventEmitter, tableUrlParams, getData, fetchLoading]);

    // get the sort column and order from the url
    const getInitialSorting = useMemo(() => {
      if (tableUrlParams['ordering']) {
        let colName = tableUrlParams['ordering'];
        let desc = false;
        if (tableUrlParams['ordering'].indexOf('-') === 0) {
          colName = tableUrlParams['ordering'].substring(1);
          desc = true;
        }
        return [{
          id: colName,
          desc
        }];
      } else if (sortKeys?.length === 2 && tableUrlParams[sortKeys[0]] && tableUrlParams[sortKeys[1]]) {
        return [{
          id: tableUrlParams[sortKeys[1]],
          desc: tableUrlParams[sortKeys[0]] === 'desc'
        }];
      }
      return [];
    }, [tableUrlParams, sortKeys]);

    // get pageData for pagination component
    const getPageData = useMemo(() => ({
      page: tableUrlParams.page,
      page_size: tableUrlParams.page_size,
      total: data.count
    }), [tableUrlParams, data]);

    // handle sorting for employee table
    const onSortChange = useCallback(({
      sortBy
    }) => {
      if (!sortBy.length) {
        if (!sortKeys) return {
          ordering: null
        };else if (sortKeys?.length === 2) return {
          [sortKeys[0]]: null,
          [sortKeys[1]]: null
        };
      } else {
        const [{
          id,
          desc
        }] = sortBy;
        if (!sortKeys) return {
          ordering: desc ? '-' + id : id
        };else if (sortKeys?.length === 2) {
          return {
            [sortKeys[0]]: desc ? 'desc' : 'asc',
            [sortKeys[1]]: id
          };
        }
      }
    }, [sortKeys]);
    const updateRow = useCallback(row => {
      return update(row).catch(err => {
        errorToast(err?.errors?.title || 'Failed to updated the record!');
        throw err;
      });
    }, [update, errorToast]);
    const updateCellData = useDeepCompareCallback((row, columnId, value) => {
      if (row.pk) {
        updateRow(_.set({
          ...row
        }, columnId, value)).then(response => {
          setData(prevData => {
            const data = prevData.data.map(item => {
              if (item.pk !== row.pk) return item;
              return {
                ...row,
                ...response
              };
            });
            return {
              data,
              count: prevData.count
            };
          });
          successToast('Successfully updated the record!');
        }).catch(err => console.log('err', err));
      } else if (newRows[row._type]) {
        // check for its validation if present
        const validator = addRowOptions?.validate?.[columnId];
        let errors = {
          ...newRows[row._type].errors
        };
        delete errors[columnId];
        if (errors.title) delete errors.title;
        if (errors.status) delete errors.status;
        if (errors.field_error) delete errors.field_error;
        if (typeof validator === 'function') {
          const isValid = validator(value);
          if (typeof isValid === 'string') {
            errors[columnId] = isValid;
          }
        }
        const newRow = {
          ...newRows[row._type],
          errors,
          ..._.set({}, columnId, value)
        };
        setNewRows({
          ...newRows,
          [row._type]: {
            ...newRow
          }
        });
        setData(prevData => {
          const data = prevData.data.map(item => {
            if (item._type !== row._type) return item;
            return newRow;
          });
          return {
            data,
            count: prevData.count
          };
        });

        // SAVE THE ROW IF IT IS ADD ROW AND SAVEON PROP IS THERE...
        if (_.isEmpty(newRow.errors, true) && addRowOptions?.saveOn === columnId) {
          // USING ETTIMEOUT SO THAT IT CAN READ THE VALUE OF SAVEON COLUMN
          window.setTimeout(() => {
            grandEmitter.emit(`${queryKey}_SAVE_NEW_ROW`, {
              index: _.findIndex(data.data, {
                _type: row._type
              }),
              _type: row?._type
            });
          }, 100);
        }
      }
    }, [data, grandEmitter, queryKey, successToast, updateRow, addRowOptions?.saveOn, addRowOptions?.validate, newRows]);
    const handleExportClick = async (type, name, dataCount) => {
      try {
        if (dataCount <= 5000) successToast(`Download initiated...!`);else successToast(`File is being generated, please check notifications in a while!`);
        const {
          page,
          page_size,
          ...rest
        } = tableUrlParams;
        const data = await fetch({
          ...rest,
          export: type,
          sync: dataCount > 5000 ? 'no' : 'yes',
          is_export: true
        });
        if (dataCount <= 5000) downloadFile(data, `${name || 'Export'}.${type}`);
      } catch (error) {
        errorToast(error?.errors?.title ?? 'Unable to process your request!');
      }
    };
    const handleRefresh = config => {
      // either send listEmpty=true or page, in case both are passed, page will be given preference
      let {
        page = tableUrlParams.page,
        listEmpty = false,
        bulkDeleted = undefined
      } = config ?? {};

      // if we need to change the page, we save the config, else just fetch the data
      if (page !== tableUrlParams.page) {
        let totalPages = Math.ceil(data.count / tableUrlParams.page_size);
        navigateToParams({
          page: page > totalPages ? totalPages : page
        });
      }
      let newPage = page;
      const actualSize = data.data.length;
      if (bulkDeleted) {
        listEmpty = actualSize === bulkDeleted;
      }
      if (listEmpty) {
        newPage = newPage === 1 ? newPage : newPage - 1;
        if (newPage === 1 && tableUrlParams.page !== 1) navigateToParams({
          page: newPage
        });else getData(tableUrlParams);
      } else getData(tableUrlParams);
    };
    const onEdit = useCallback(row => {
      if (typeof editHandler === 'function') {
        editHandler(row);
        return true;
      }
      if (typeof haveEditPermission === 'string') {
        const id = row?.pk ?? 'add';
        const label = row?.name;
        const pathname = haveEditPermission.replace(':id', id);
        const search = label && '?h=' + label;
        const path = {
          label,
          pathname,
          search
        };
        if (editInNewTab === true) {
          grandEmitter.emit('ADD_TAB', path);
        } else {
          history.push({
            pathname: path.pathname,
            search: path.search
          });
        }
      }
    }, [editHandler, editInNewTab, grandEmitter, history, haveEditPermission]);
    const rowClicked = useMemo(() => {
      if (disableRowClicked) return undefined;
      if (!rowClickHandler && !haveEditPermission && !editHandler) return undefined;
      return row => {
        if (rowClickHandler) {
          rowClickHandler(row);
        } else if (haveEditPermission || editHandler) onEdit(row?.original);
      };
    }, [disableRowClicked, rowClickHandler, haveEditPermission, editHandler, onEdit]);
    useEffect(() => {
      const subscription = grandEmitter?.getObservable()?.subscribe(event => {
        switch (event.type) {
          case `${queryKey}_DELETE_ROW`:
            {
              const {
                pk,
                _type
              } = event.data;
              if (_type) {
                setData(({
                  data,
                  count
                }) => {
                  let newData = data.filter(r => r._type !== _type);
                  return {
                    data: [...newData],
                    count
                  };
                });
                setNewRows(data => {
                  const newData = {
                    ...data
                  };
                  delete newData[_type];
                  return newData;
                });
              } else if (pk) {
                setData(({
                  data,
                  count
                }) => {
                  let newData = data.filter(r => r.pk !== pk);
                  if (newData.length === 0) grandEmitter.emit(`${queryKey}__REFRESH_EVENT`, {
                    listEmpty: true
                  });
                  return {
                    data: [...newData],
                    count: count - 1
                  };
                });
              }
              break;
            }
          case `EXPORT`:
            {
              const {
                type,
                name
              } = event.data;
              handleExportClick(type, name, data.count);
              break;
            }
          case `${queryKey}__REORDER_EVENT`:
            setData(prev => ({
              data: event.data,
              count: prev.count
            }));
            break;
          case `${queryKey}__PAGINATION_EVENT`:
            navigateToParams(event.data);
            break;
          case `${queryKey}__REFRESH_EVENT`:
            handleRefresh(event.data);
            break;
          case `${queryKey}__SORT_EVENT`:
            onSortChange(event.data);
            break;
          case `${queryKey}__TABLE_STATE_EVENT`:
            navigateToParams({
              state: event.data,
              ...onSortChange(event.data)
            });
            break;
          case `${queryKey}__REORDER_SOURCE_EVENT`:
            setData(prev => {
              const newData = [...prev.data];
              newData.splice(event.data.startIndex, 1);
              return {
                data: newData,
                count: prev.count - 1
              };
            });
            break;
          case `${queryKey}__REORDER_DESTINATION_EVENT`:
            setData(prev => {
              const newData = [...prev.data];
              newData.splice(event.data.endIndex, 0, event.data.movedRow);
              return {
                data: newData,
                count: newData.length
              };
            });
            break;
          case `${queryKey}__REORDER_COMMON_EVENT`:
            setData(prev => {
              const newData = [...prev.data];
              const [movedRow] = newData.splice(event.data.startIndex, 1);
              newData.splice(event.data.endIndex, 0, movedRow);
              return {
                data: newData,
                count: newData.length
              };
            });
            break;
          default:
            break;
        }
      });
      return () => subscription?.unsubscribe();
    }, [grandEmitter, queryKey, newRows, navigateToParams, updateRow, addRowOptions, data, handleRefresh]);
    useEffect(() => {
      const subscription = grandEmitter?.getObservable()?.subscribe(event => {
        switch (event.type) {
          case `${queryKey}__GET_TABLE_DATA_EVENT`:
            grandEmitter.emit(`${queryKey}__TABLE_DATA`, data?.data ?? []);
            break;
          case `${queryKey}__GET_TABLE_DATA_ON_DROP`:
            grandEmitter.emit(`${queryKey}__TABLE_DATA_ON_DROP`, data?.data ?? []);
            break;
          default:
            break;
        }
      });
      return () => subscription?.unsubscribe();
    }, [grandEmitter, queryKey, data]);
    const getRowId = useCallback((row, relativeIndex) => row.uniqueId ? row.uniqueId : row?.pk ? row?.pk : relativeIndex, []);
    if (fetchLoading) return /*#__PURE__*/React.createElement(Box, {
      fill: true,
      align: "center",
      justify: "center"
    }, /*#__PURE__*/React.createElement(Loader, null));
    return /*#__PURE__*/React.createElement(Box, {
      flex: {
        grow: 1
      },
      style: {
        maxHeight: '100%'
      }
    }, showFilters && /*#__PURE__*/React.createElement(Filters, {
      queryKey: queryKey,
      searchPlaceholder: searchPlaceholder,
      config: filters //send empty array if only search is required
      ,
      canSearch: searchDisabled //if search is not required
      ,
      searchWidth: searchWidth,
      onChange: onChange,
      setLocalFilter: setLocalFilter,
      withoutQuery: withoutQuery,
      initialState: tableUrlParams,
      validations: validations,
      replaceSearch: replaceSearch,
      replaceSearchWith: replaceSearchWith
    }), loading || isLoading ? /*#__PURE__*/React.createElement(Box, {
      fill: true,
      align: "center",
      justify: "center"
    }, /*#__PURE__*/React.createElement(Loader, null)) : /*#__PURE__*/React.createElement(Table, _extends({
      id: queryKey,
      queryKey: queryKey,
      data: data.data,
      initialState: preferences.config?.state ?? {},
      columns: tableCols,
      setColumns: setColumns,
      expandable: true,
      initialSortOrder: getInitialSorting,
      setSelected: setSelected,
      pageData: getPageData,
      showSizeChanger: true
      // onPageChange={navigateToParams}
      ,
      updateCellData: updateCellData,
      pageSize: pageSize,
      selected: selected
      // onRefresh={handleRefresh}
      ,
      bulkActions: bulkActions,
      deleteBulk: deleteBulk,
      label: label,
      rowClickHandler: rowClicked,
      visibleColumns: visibleColumns,
      canColumnsBeHidden: canColumnsBeHidden,
      showHideColsHandler: showHideColsHandler,
      hiddenColumnArrayList: hiddenColumnArrayList,
      disableHideCells: disableHideCells,
      addHandler: addRowOptions,
      actionIconColor: actionIconColor,
      newRows: newRows,
      haveEditPermission: haveEditPermission,
      noRecordMsg: noRecordMsg,
      maxHeight: maxHeight,
      setData: setData,
      eventEmitter: eventEmitter,
      columnsHideable: false,
      draggable: true,
      disableInternalDndContext: true,
      showStrippedRow: false,
      hideDefaultDraggable: true,
      getRowId: getRowId,
      style: {
        ...(dataTableProps.isNested ? {} : tableStyle)
      }
    }, rest)), sideLayer && /*#__PURE__*/React.createElement(SideLayer, {
      data: sideLayer,
      setShow: showSideLayer,
      content: sideContent,
      eventEmitter: eventEmitter,
      sideFunctions: sideFunctions
    }));
  };
  Component.defaultProps = dataTableDefaultProps;
  Component.propTypes = {
    columns: PropTypes.array.isRequired,
    fetch: PropTypes.func.isRequired,
    filters: PropTypes.array,
    showFilters: PropTypes.bool,
    queryKey: PropTypes.string,
    editHandler: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
    deleteHandler: PropTypes.func,
    actionIconColor: PropTypes.string,
    moreActionItems: PropTypes.array,
    searchPlaceholder: PropTypes.string,
    handleImport: PropTypes.func,
    exportable: PropTypes.array,
    importable: PropTypes.bool,
    sideContent: PropTypes.func,
    columnWidth: PropTypes.string,
    withoutQuery: PropTypes.bool,
    editInNewTab: PropTypes.bool,
    canColumnsBeHidden: PropTypes.bool,
    showHideColsHandler: PropTypes.func.isRequired,
    hiddenColumnArrayList: PropTypes.array,
    eventEmitter: PropTypes.object,
    update: PropTypes.func,
    overridePermission: PropTypes.string,
    pageSize: PropTypes.number,
    bulkDeleteHandler: PropTypes.func,
    deleteBulk: PropTypes.any,
    sideFunctions: PropTypes.any,
    label: PropTypes.string,
    bulkActions: PropTypes.array,
    rowClickHandler: PropTypes.func,
    newTabURL: PropTypes.string,
    onChange: PropTypes.func,
    initialState: PropTypes.object,
    addRowOptions: PropTypes.any,
    panelPermissions: PropTypes.object,
    sortKeys: PropTypes.array,
    noRecordMsg: PropTypes.string,
    disableRowClicked: PropTypes.bool,
    isLoading: PropTypes.bool,
    disableHideCells: PropTypes.array,
    autoSavePreference: PropTypes.bool,
    excludeFromPreference: PropTypes.array,
    replaceSearch: PropTypes.bool,
    replaceSearchWith: PropTypes.node
  };
  return /*#__PURE__*/memo(Component);
};
const tableStyle = {
  flexGrow: '1',
  height: '0px',
  flexBasis: 'auto',
  overflow: 'hidden auto'
};
export default withMultiDnd;